import React, { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteOption } from '@sravni/react-design-system/dist/types/components/Autocomplete/types/Autocomplete.types';
import { Col, Row } from '@sravni/react-design-system/lib/Grid';
import { useBoolean, useIsPhone, useStyles } from '@sravni/react-utils';
import { getOrganizationHelpers } from '@sravni/ugc/lib/organizations/helpers';
import { supportedOrganizationTypes } from '@sravni/ugc/lib/organizations/maps';

import { CUSTOMER_TYPES } from '../../../../../constants/reviews';
import { getOrganizationsOptions } from '../../../../../helpers/getOrganizationsOptions';
import { useCurrentService, useProductList, useServiceList } from '../../../../../store/hooks/createReviewData';
import { useRegions } from '../../../../../store/hooks/locations';
import { useOrganizationsList } from '../../../../../store/hooks/organizations';
import { useRatingSravni } from '../../../../../store/hooks/ratings';
import { ERROR_DICTIONARY, ERRORS } from '../../../../../utils/errorDictionary';
import { FormControl } from '../../../../UI/FormControl';
import { TUserChangeHandler } from '../../../../UI/FormControl/types';
import { REVIEW_FIELDS } from '../../../constants/common';
import { TFormInputs } from '../../../types';
import { makeLabelRequired } from '../../../utils/makeLabelRequired';
import { Step } from '../Step/Step';

import { LEGAL_OPTIONS } from './constants';
import styles from './organization-and-service-step.module.scss';

type TProps = {
  isReadOnly: boolean;
  handleUserChange?: TUserChangeHandler;
};

const reviewObjectTypeOptions: AutocompleteOption[] = supportedOrganizationTypes.map((orgType) => {
  const label = getOrganizationHelpers(orgType).namesType.bigFirstChar;

  return {
    label,
    value: orgType,
  };
});

export const OrganizationAndServiceStep: React.FC<TProps> = (params) => {
  const { isReadOnly, handleUserChange } = params;
  const {
    control,
    setError,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext();

  const cx = useStyles(styles);
  const isPhone = useIsPhone();

  const ratingSravni = useRatingSravni();
  const organizationList = useOrganizationsList();
  const serviceList = useServiceList();
  const productList = useProductList();
  const regions = useRegions();

  const [isCustomerTypeRadioVisible, setIsCustomerTypeRadioVisible] = useBoolean(false);

  const organizationIdsOptions = useMemo(
    () => (organizationList ? getOrganizationsOptions(organizationList, ratingSravni) : []),
    [organizationList, ratingSravni],
  );

  const locationOptions = useMemo(
    () =>
      Object.values(regions)
        .map((region) => ({
          label: region.name,
          value: region.garId,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ru', { sensitivity: 'base' })),
    [regions],
  );

  const serviceOptions = useMemo(
    () =>
      serviceList?.map((service) => ({
        label: service.label,
        value: service.key,
      })) ?? [],
    [serviceList],
  );

  const productOptions = useMemo(
    () =>
      productList?.map((product) => ({
        label: product.name,
        value: product.id,
      })) ?? [],
    [productList],
  );

  useEffect(() => {
    if (!organizationIdsOptions.length) return;

    const { [REVIEW_FIELDS.ORGANIZATION_ID]: organizationId } = getValues();

    if (!organizationId) return;

    const orgInNewOptions = organizationIdsOptions.find(({ value }) => value === organizationId);

    if (!orgInNewOptions) {
      setValue(REVIEW_FIELDS.ORGANIZATION_ID, null);
    }
  }, [getValues, organizationIdsOptions, setValue]);

  useEffect(() => {
    if (!serviceOptions.length) return;

    const { [REVIEW_FIELDS.SERVICE]: service } = getValues();

    if (!service) return;

    const serviceInNewOptions = serviceOptions.find(({ value }) => value === service);

    if (!serviceInNewOptions) {
      setValue(REVIEW_FIELDS.SERVICE, null);
    }
  }, [getValues, serviceOptions, setValue]);

  useEffect(() => {
    if (!productOptions.length) return;

    const { [REVIEW_FIELDS.PRODUCT_ID]: productId } = getValues();

    if (!productId) return;

    const productInNewOptions = productOptions.find(({ value }) => value === productId);

    if (!productInNewOptions) {
      setValue(REVIEW_FIELDS.PRODUCT_ID, null);
    }
  }, [getValues, productOptions, setValue]);

  const service = useCurrentService();

  useEffect(() => {
    if (!service) {
      setIsCustomerTypeRadioVisible.off();
      setValue(REVIEW_FIELDS.IS_LEGAL, null);
      return;
    }

    const newCustomerType = service.customerType;

    if (newCustomerType === CUSTOMER_TYPES.All) {
      setIsCustomerTypeRadioVisible.off();
      setValue(REVIEW_FIELDS.IS_LEGAL, null);
      return;
    }

    if (newCustomerType === CUSTOMER_TYPES.Both) {
      setIsCustomerTypeRadioVisible.on();
      const { [REVIEW_FIELDS.IS_LEGAL]: isLegal } = getValues();
      setValue(REVIEW_FIELDS.IS_LEGAL, isLegal ?? CUSTOMER_TYPES.Individuals);
      return;
    }

    setIsCustomerTypeRadioVisible.off();
    setValue(REVIEW_FIELDS.IS_LEGAL, service.customerType ?? null);
  }, [getValues, service, setIsCustomerTypeRadioVisible, setValue]);

  const isProductIdDisabled = productList && productList.length === 0; // оставляем доступным для фокуса, если не загружен список продуктов

  const checkOrgTypeSelected = useCallback(
    (focusedField: keyof TFormInputs) => {
      const { [REVIEW_FIELDS.ORGANIZATION_TYPE]: organizationType } = getValues();

      if (!organizationType) {
        setError(focusedField, { message: ERROR_DICTIONARY[ERRORS.CHOOSE_ORG_TYPE], type: 'custom' });
      }
    },
    [getValues, setError],
  );

  const handleFocusOrganizationId = useCallback(() => {
    checkOrgTypeSelected(REVIEW_FIELDS.ORGANIZATION_ID);
  }, [checkOrgTypeSelected]);

  const handleFocusReviewTag = useCallback(() => {
    checkOrgTypeSelected(REVIEW_FIELDS.SERVICE);
  }, [checkOrgTypeSelected]);

  const handleFocusProductId = useCallback(() => {
    const {
      [REVIEW_FIELDS.ORGANIZATION_TYPE]: organizationType,
      [REVIEW_FIELDS.ORGANIZATION_ID]: organization,
      [REVIEW_FIELDS.SERVICE]: service,
    } = getValues();

    if (!organizationType || !service || !organization) {
      setError(REVIEW_FIELDS.PRODUCT_ID, {
        message: ERROR_DICTIONARY[ERRORS.CHOOSE_ORG_TYPE_ORG_SERVICE],
        type: 'custom',
      });
    }
  }, [getValues, setError]);

  return (
    <Step title={'О какой организации и услуге отзыв'} className={'h-mt-8'}>
      <Row gutter={[16, 24]} className={cx('row')}>
        <Col span={isPhone ? 12 : 4}>
          <FormControl
            controlType="autocomplete"
            name={REVIEW_FIELDS.ORGANIZATION_TYPE}
            control={control}
            options={reviewObjectTypeOptions}
            label={makeLabelRequired('Тип организации')}
            error={errors?.[REVIEW_FIELDS.ORGANIZATION_TYPE]}
            optionsSort={false}
            disabled={isReadOnly}
            handleUserChange={handleUserChange}
          />
        </Col>
        <Col span={isPhone ? 12 : 4}>
          <FormControl
            controlType="autocomplete"
            name={REVIEW_FIELDS.ORGANIZATION_ID}
            control={control}
            options={organizationIdsOptions}
            label={makeLabelRequired('Название')}
            error={errors?.[REVIEW_FIELDS.ORGANIZATION_ID]}
            onFocus={handleFocusOrganizationId}
            optionsSort={false}
            disabled={isReadOnly}
            handleUserChange={handleUserChange}
          />
        </Col>
        <Col span={isPhone ? 12 : 4}>
          <FormControl
            controlType="autocomplete"
            name={REVIEW_FIELDS.LOCATION_GAR_ID}
            control={control}
            options={locationOptions}
            label={makeLabelRequired('Регион')}
            error={errors?.[REVIEW_FIELDS.LOCATION_GAR_ID]}
            optionsSort={false}
            disabled={isReadOnly}
            handleUserChange={handleUserChange}
          />
        </Col>
        <Col span={isPhone ? 12 : 4}>
          <FormControl
            controlType="autocomplete"
            name={REVIEW_FIELDS.SERVICE}
            control={control}
            options={serviceOptions}
            label={makeLabelRequired('Выберите услугу')}
            error={errors?.[REVIEW_FIELDS.SERVICE]}
            onFocus={handleFocusReviewTag}
            optionsSort={false}
            disabled={isReadOnly}
            handleUserChange={handleUserChange}
          />
        </Col>
        {isCustomerTypeRadioVisible && (
          <Col span={isPhone ? 12 : 4}>
            <FormControl
              controlType="radio-button"
              name={REVIEW_FIELDS.IS_LEGAL}
              control={control}
              options={LEGAL_OPTIONS}
              disabled={isReadOnly}
              size={52}
              handleUserChange={handleUserChange}
            />
          </Col>
        )}
        <Col span={isCustomerTypeRadioVisible || isPhone ? 12 : 8}>
          <FormControl
            controlType="autocomplete"
            name={REVIEW_FIELDS.PRODUCT_ID}
            control={control}
            options={productOptions}
            label={isProductIdDisabled ? 'Нет доступных продуктов' : 'Продукт'}
            error={errors?.[REVIEW_FIELDS.PRODUCT_ID]}
            disabled={isReadOnly || isProductIdDisabled}
            optionsSort={false}
            onFocus={handleFocusProductId}
            handleUserChange={handleUserChange}
          />
        </Col>
      </Row>
    </Step>
  );
};
